import { useCallback, useEffect, useRef, useState } from 'react';

interface UseResizeHandleOptions {
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number | (() => number);
  onWidthChange?: (width: number) => void; // 선택적 콜백
}

export const useResizeHandle = (options: UseResizeHandleOptions = {}) => {
  const {
    initialWidth = 400,
    minWidth = 200,
    maxWidth = () => window.innerWidth * 0.6,
    onWidthChange,
  } = options;

  const [isResizing, setIsResizing] = useState(false);
  const widthRef = useRef(initialWidth);
  const dragOffsetRef = useRef(0);
  const elementRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    if (!elementRef.current) return;

    // 현재 실제 너비를 가져옴 (DOM에서)
    const currentWidth = elementRef.current.offsetWidth;
    widthRef.current = currentWidth;

    // 드래그 시작할 때의 마우스 위치와 현재 너비의 차이를 계산
    const offset = e.clientX - currentWidth;
    dragOffsetRef.current = offset;
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !elementRef.current) return;

      // requestAnimationFrame으로 성능 최적화
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        if (!elementRef.current) return;

        // 드래그 오프셋을 고려한 새로운 너비 계산
        const newWidth = e.clientX - dragOffsetRef.current;
        const currentMaxWidth =
          typeof maxWidth === 'function' ? maxWidth() : maxWidth;

        if (newWidth >= minWidth && newWidth <= currentMaxWidth) {
          // 직접 DOM 스타일 조작으로 성능 향상
          elementRef.current.style.width = `${newWidth}px`;
          widthRef.current = newWidth;

          // 선택적으로 콜백 호출 (필요한 경우에만)
          onWidthChange?.(newWidth);
        }
      });
    },
    [isResizing, minWidth, maxWidth, onWidthChange],
  );

  const handleMouseUp = useCallback(() => {
    if (!isResizing) return;

    setIsResizing(false);
    dragOffsetRef.current = 0;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // 드래그 종료 시 최종 너비를 콜백으로 알림
    if (elementRef.current) {
      const finalWidth = elementRef.current.offsetWidth;
      widthRef.current = finalWidth;
      onWidthChange?.(finalWidth);
    }
  }, [isResizing, onWidthChange]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // 초기 너비 설정
  useEffect(() => {
    if (elementRef.current) {
      elementRef.current.style.width = `${widthRef.current}px`;
      onWidthChange?.(widthRef.current);
    }
  }, [onWidthChange]);

  return {
    isResizing,
    elementRef,
    handleMouseDown,
    getCurrentWidth: () => widthRef.current, // 현재 너비 조회 함수 제공
  };
};
